// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/contador/contadorService.ts
//  Chamadas do Portal do Contador (/api/contador/*). Ver ContadorPortalController.
// ════════════════════════════════════════════════════════════════════════════

import { contadorClient, type ContadorUsuario } from './contadorClient';
import type { AnaliseFiscal } from '@/features/cofre/analiseFiscal';
import type { CarteiraCliente, ComparacaoRegime, Faturamento, ParametrosRegime } from '@/features/cofre/faturamento';
import type { ParametrosReforma, ReformaConferencia, ReformaSimulacao } from '@/features/cofre/reforma';
import type { ResumoEnvio } from '@/features/cofre/EnviarXmlsBotao';

/** A API manda os enums como número (CofreSentido / CofreSituacao). */
export const SENTIDO = { Entrada: 0, Saida: 1 } as const;
export type Sentido = (typeof SENTIDO)[keyof typeof SENTIDO];
export const SITUACAO = { Autorizada: 1, Cancelada: 2, Denegada: 3 } as const;
export type Situacao = (typeof SITUACAO)[keyof typeof SITUACAO];
export const ROTULO_SITUACAO: Record<Situacao, string> = { 1: 'Autorizada', 2: 'Cancelada', 3: 'Denegada' };

export interface ClienteResumo {
  contribuinteId: number;
  cnpjCpf: string;
  razaoSocial?: string | null;
  uf?: string | null;
  documentos: number;
  saidas: number;
  entradas: number;
  canceladas: number;
  valorSaidas: number;
  valorEntradas: number;
  ultimoRecebido?: string | null;
}

/** SPED Fiscal ou Sintegra gerado a partir do cofre; conteudo em base64 (Latin-1). */
export interface ArquivoFiscal {
  nome: string;
  conteudo: string;
  avisos: string[];
  documentos: number;
  linhas: number;
}

export interface Pendencia {
  contribuinteId: number;
  cliente: string;
  tipo: 'simples' | 'envio' | 'numeracao' | 'fiscal' | 'reforma';
  gravidade: 'alta' | 'media';
  mensagem: string;
}

export interface ResumoSemana {
  competencia: number;
  mesFechado: number;
  clientes: number;
  pendencias: Pendencia[];
  receberPorEmail: boolean;
}

export interface DocumentoLinha {
  id: number;
  modelo: number;
  sentido: Sentido;
  chave: string;
  numero: number;
  serie?: string | null;
  dhEmissao: string;
  participanteCnpjCpf?: string | null;
  participanteNome?: string | null;
  cfopPrincipal?: string | null;
  valorTotal: number;
  valorIcms: number;
  valorSt: number;
  valorIpi: number;
  valorIss: number;
  situacao: Situacao;
}

export interface DocumentosPagina {
  itens: DocumentoLinha[];
  total: number;
  valorTotal: number;
  pagina: number;
  tamanhoPagina: number;
}

export interface Conferencia {
  competencia: number;
  canceladas: number;
  denegadas: number;
  eventosSemDocumento: number;
  ultimoRecebido?: string | null;
  numeracaoFaltante: { modelo: number; serie: string; inicio: number; fim: number; quantidade: number }[];
  inutilizacoes: {
    id: number; modelo: number; serie: string; numeroInicial: number; numeroFinal: number;
    protocolo?: string | null; justificativa?: string | null; dhRecebimento: string;
  }[];
}

export interface FiltroDocumentos {
  competencia: number;
  modelo?: number;
  sentido?: Sentido;
  situacao?: Situacao;
  busca?: string;
  pagina?: number;
  tamanhoPagina?: number;
}

const MODELOS: Record<number, string> = { 55: 'NF-e', 65: 'NFC-e', 59: 'CF-e SAT', 57: 'CT-e', 67: 'CT-e OS', 58: 'MDF-e', 99: 'NFS-e' };
export const nomeModelo = (m: number) => MODELOS[m] ?? String(m);
export const MODELOS_FILTRO = Object.entries(MODELOS).map(([valor, rotulo]) => ({ valor: Number(valor), rotulo }));

/** aaaamm do mês atual (hora local). */
export const competenciaAtual = () => {
  const d = new Date();
  return d.getFullYear() * 100 + d.getMonth() + 1;
};

/** aaaamm do mês anterior: o último mês fechado, que é o que o contador apura. */
export const competenciaFechada = () => {
  const d = new Date();
  const m = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return m.getFullYear() * 100 + m.getMonth() + 1;
};

/** "Outubro de 2026" (só a primeira letra maiúscula). */
export const rotuloCompetencia = (c: number) => {
  const s = new Date(Math.floor(c / 100), (c % 100) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** Últimos N meses, do mais recente para o mais antigo. */
export const ultimasCompetencias = (n = 18) => {
  const d = new Date();
  return Array.from({ length: n }, (_, i) => {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    return m.getFullYear() * 100 + m.getMonth() + 1;
  });
};

export const formatarDoc = (doc?: string | null) => {
  const d = (doc ?? '').replace(/\D/g, '');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return doc ?? '';
};

function baixar(blob: Blob, nome: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

/** Erro de um download (blob): a mensagem da API vem dentro do blob. */
async function mensagemDoBlob(erro: unknown): Promise<string> {
  const data = (erro as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).mensagem ?? 'Não foi possível baixar o arquivo.';
    } catch {
      /* não era JSON */
    }
  }
  return erro instanceof Error ? erro.message : 'Não foi possível baixar o arquivo.';
}

async function baixarDe(url: string, nome: string, params?: Record<string, unknown>) {
  try {
    const r = await contadorClient.get(url, { params, responseType: 'blob', timeout: 300_000 });
    baixar(r.data, nome);
  } catch (e) {
    throw new Error(await mensagemDoBlob(e));
  }
}

export const contadorService = {
  async entrar(email: string, senha: string) {
    const r = await contadorClient.post<{ accessToken: string; usuario: ContadorUsuario }>(
      '/api/contador/auth/login', { email, senha });
    return r.data;
  },
  definirSenha: (token: string, novaSenha: string) =>
    contadorClient.post('/api/contador/auth/definir-senha', { token, novaSenha }),
  esqueciSenha: (email: string) => contadorClient.post('/api/contador/auth/esqueci-senha', { email }),

  async clientes(competencia: number) {
    const r = await contadorClient.get<{ competencia: number; clientes: ClienteResumo[] }>(
      '/api/contador/clientes', { params: { competencia } });
    return r.data;
  },
  async documentos(contribuinteId: number, f: FiltroDocumentos) {
    const r = await contadorClient.get<DocumentosPagina>(`/api/contador/clientes/${contribuinteId}/documentos`, { params: f });
    return r.data;
  },
  async conferencia(contribuinteId: number, competencia: number) {
    const r = await contadorClient.get<Conferencia>(`/api/contador/clientes/${contribuinteId}/conferencia`, { params: { competencia } });
    return r.data;
  },

  async carteira(competencia: number) {
    const r = await contadorClient.get<{ competencia: number; clientes: CarteiraCliente[] }>(
      '/api/contador/carteira', { params: { competencia } });
    return r.data;
  },
  async faturamento(contribuinteId: number, competencia: number) {
    const r = await contadorClient.get<Faturamento>(`/api/contador/clientes/${contribuinteId}/faturamento`, { params: { competencia } });
    return r.data;
  },
  async regimes(contribuinteId: number, competencia: number, p: ParametrosRegime) {
    const r = await contadorClient.get<ComparacaoRegime>(`/api/contador/clientes/${contribuinteId}/regimes`, { params: { competencia, ...p } });
    return r.data;
  },

  async reforma(contribuinteId: number, competencia: number) {
    const r = await contadorClient.get<ReformaConferencia>(`/api/contador/clientes/${contribuinteId}/reforma`, { params: { competencia } });
    return r.data;
  },
  async reformaSimulacao(contribuinteId: number, competencia: number, p: ParametrosReforma) {
    const r = await contadorClient.get<ReformaSimulacao>(`/api/contador/clientes/${contribuinteId}/reforma/simulacao`, { params: { competencia, ...p } });
    return r.data;
  },

  async arquivoFiscal(contribuinteId: number, tipo: 'sped-fiscal' | 'sintegra', competencia: number, opcoes: { perfil?: string; finalidade?: number }) {
    const r = await contadorClient.get<ArquivoFiscal>(`/api/contador/clientes/${contribuinteId}/${tipo}`,
      { params: { competencia, ...opcoes }, timeout: 300_000 });
    const bytes = Uint8Array.from(atob(r.data.conteudo), (c) => c.charCodeAt(0));
    baixar(new Blob([bytes], { type: 'text/plain;charset=iso-8859-1' }), r.data.nome);
    return r.data;
  },

  async resumo() {
    const r = await contadorClient.get<ResumoSemana>('/api/contador/resumo', { timeout: 120_000 });
    return r.data;
  },
  preferencias: (receberResumoSemanal: boolean) => contadorClient.put('/api/contador/preferencias', { receberResumoSemanal }),
  baixarRelatorio: (contribuinteId: number, cnpj: string, competencia: number, observacao?: string) =>
    baixarDe(`/api/contador/clientes/${contribuinteId}/relatorio`, `relatorio_${cnpj}_${competencia}.pdf`,
      { competencia, observacao: observacao?.trim() || undefined }),

  async analises(contribuinteId: number, competencia: number, anexo?: number) {
    const r = await contadorClient.get<AnaliseFiscal>(`/api/contador/clientes/${contribuinteId}/analises`, { params: { competencia, anexo } });
    return r.data;
  },

  async enviarXmls(contribuinteId: number, arquivo: File) {
    const form = new FormData();
    form.append('arquivo', arquivo);
    const r = await contadorClient.post<ResumoEnvio>(`/api/contador/clientes/${contribuinteId}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600_000,
    });
    return r.data;
  },

  baixarXml: (d: DocumentoLinha) => baixarDe(`/api/contador/documentos/${d.id}/xml`, `${d.chave}.xml`),
  baixarInutilizacao: (id: number) => baixarDe(`/api/contador/inutilizacoes/${id}/xml`, `inutilizacao-${id}.xml`),
  async abrirDanfe(d: DocumentoLinha) {
    try {
      const r = await contadorClient.get(`/api/contador/documentos/${d.id}/danfe`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      throw new Error(await mensagemDoBlob(e));
    }
  },
  baixarZip: (contribuinteId: number, cnpj: string, competencia: number, modelo?: number, sentido?: Sentido) =>
    baixarDe(`/api/contador/clientes/${contribuinteId}/zip`, `${cnpj}_${competencia}.zip`, { competencia, modelo, sentido }),
};
